/**
 * @file DeletePageDialog.tsx
 * @description Окно «Удалить страницу» (phase19.16 Р3а → Р3в, v0.19.64;
 * SB128, SB131; контракт r3v-api-contract §3 «Удаление страницы с
 * переадресацией»): последствия и выбор, что будет со старым адресом —
 * «Переадресовать на» страницу, товар, категорию, статью или адрес
 * (ручное правило 301, право публикации контента) или «Без переадресации —
 * старые ссылки перестанут работать». Страница, которой не было на сайте,
 * адреса не имеет — выбора нет. До sm — на весь экран, кнопки снизу.
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { CheckRow } from "../../site-view/CheckRow";
import { RedirectTargetField } from "../../site-seo/redirects/RedirectTargetField";
import type { SiteRedirectTargetInput } from "../../../lib/api-site-redirects";
import { EMPTY_REDIRECT_TARGET, redirectTargetInput, redirectTargetProblem, type RedirectTargetValue } from "../../../lib/site-seo/redirect-target";

interface Props {
  open: boolean;
  title: string;
  pageId: string;
  /** Адрес на сайте (`/about`); `null` — страницы не было на сайте. */
  liveAddress: string | null;
  busy: boolean;
  /** Ошибка сервера (цель переадресации, удаление). */
  error: string | null;
  onCancel: () => void;
  onConfirm: (redirect: SiteRedirectTargetInput | null) => void;
}

export function DeletePageDialog({ open, title, pageId, liveAddress, busy, error, onCancel, onConfirm }: Props) {
  const [redirect, setRedirect] = useState(true);
  const [target, setTarget] = useState<RedirectTargetValue>(EMPTY_REDIRECT_TARGET);
  const [targetError, setTargetError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRedirect(true);
    setTarget(EMPTY_REDIRECT_TARGET);
    setTargetError(null);
  }, [open]);

  function submit() {
    if (!liveAddress || !redirect) return onConfirm(null);
    const problem = redirectTargetProblem(target);
    setTargetError(problem);
    const input = redirectTargetInput(target);
    if (!problem && input) onConfirm(input);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next && !busy ? onCancel() : null)}>
      <DialogContent fullScreen className="sm:max-h-[90vh] sm:max-w-[35rem] sm:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Удалить страницу «{title}»?</DialogTitle>
          <DialogDescription>
            Страница пропадёт с сайта сразу, пункт меню на неё скроется. Восстановить её можно 12 месяцев в «Удалённых страницах» — она вернётся скрытой.
          </DialogDescription>
        </DialogHeader>
        {liveAddress ? (
          <div className="flex min-w-0 flex-col gap-2">
            <CheckRow checked={redirect} disabled={busy} onChange={setRedirect}>
              Переадресовать со старого адреса {liveAddress}
            </CheckRow>
            {redirect ? (
              <div className="pl-7">
                <RedirectTargetField value={target} onChange={setTarget} error={targetError} disabled={busy} excludeIds={[pageId]} />
              </div>
            ) : (
              <p className="type-caption pl-7 text-warning">Без переадресации старые ссылки на {liveAddress} перестанут работать.</p>
            )}
          </div>
        ) : null}
        {error ? <NoticeStrip tone="error">{error}</NoticeStrip> : null}
        <DialogFooter className="sticky -bottom-6 z-10 -mx-6 -mb-6 max-sm:mt-auto bg-surface-lowest px-6 py-3 [box-shadow:0_-1px_0_var(--outline-variant)] flex-row justify-end">
          <Button variant="ghost" className="max-sm:flex-1" onClick={onCancel} disabled={busy}>
            Отмена
          </Button>
          <Button variant="destructive-solid" className="max-sm:flex-1" loading={busy} onClick={submit}>
            Удалить страницу
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
